import { FC, useEffect, useState } from "react";
import { extractCards, getNestedProperty, setNestedProperty } from "./Helpers";
import { MasterModel, ThemeJsonData } from "./Types";
import { Box, Breadcrumbs, Button, Card, CardContent, CircularProgress, Container, Grid, Link, Switch, Typography, useTheme } from "@material-ui/core";
import { ChevronRight } from "@material-ui/icons";
import React from "react";
import ThemeCard from "./Card";
import { getFontFamilyMaster, getFontSizeMaster, getFontWeightMaster, getIconSizeMaster, getThemeJson, getThemePageFields, updateThemeJson } from "./ConnectionService";
import AlertMessage from "src/components/ui-components/AlertMessage";
import { Helmet } from "react-helmet-async";

type ThemeTypes = "ThemeJsonLight" | "ThemeJsonDark";
const ThemesList = ["ChartTheme", "ThemeJsonLight", "ThemeJsonDark"];

const ThemeCreateEdit: FC = () => {
  const theme = useTheme();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSectionLoading, setIsSectionLoading] = useState<boolean>(false);
  const [themeJsonData, setThemeJsonData] = useState<ThemeJsonData | null>(null);
  const [distinctCards, setDistinctCards] = useState<string[]>(extractCards([]));
  const [themeType, setThemeType] = useState<ThemeTypes>("ThemeJsonLight");
  const [changesMade, setChangesMade] = useState<boolean>(false);
  const [sectionList, setSectionList] = useState([]);
  const [fontFamilyOptions, setFontFamilyOptions] = useState<MasterModel[]>([]);
  const [fontWeightOptions, setFontWeightOptions] = useState<MasterModel[]>([]);
  const [fontSizeOptions, setFontSizeOptions] = useState([]);
  const [iconSizeOptions, setIconSizeOptions] = useState([]);
  const [fieldsData, setFieldsData] = useState(null);
  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(false);

  const handleSliderChange = (value) => {
    if (value === true) {
      setThemeType("ThemeJsonDark");
      setIsDarkTheme(true);
    } else {
      setThemeType("ThemeJsonLight");
      setIsDarkTheme(false);
    }
  };

  const getFontFamilyMasterData = async () => {
    const res = await getFontFamilyMaster();
    setFontFamilyOptions(res);
  };
  
  const getFontWeightMasterData = async () => {
    const res = await getFontWeightMaster();
    setFontWeightOptions(res);
  };

  const getFontSizeMasterData = async () => {
    const res = await getFontSizeMaster();
    const fontSizes = res.map(obj => obj.FontSize);
    setFontSizeOptions(fontSizes);
  };

  const getIconSizeMasterData = async () => {
    const res = await getIconSizeMaster();
    const iconSizes = res.map(obj => obj.IconSize);
    setIconSizeOptions(iconSizes);
  };

  const fetchThemeJson = async () => {
    setIsFetching(true);

    try {
      const res = await getThemeJson();
      const themeData = res[0];
      const parsedThemeData: Partial<ThemeJsonData> = {
        ThemeJsonKey: themeData.ThemeJsonKey,
        AppKey: themeData.AppKey,
        ThemeName: themeData.ThemeName,
        ThemeJsonLight: null,
        ThemeJsonDark: null,
        ChartTheme: null,
      };

      Object.keys(themeData).forEach((key) => {
        if (ThemesList.includes(key)) {
          try {
            parsedThemeData[key as keyof ThemeJsonData] = JSON.parse(
              themeData[key]
            );
          } catch (error) {
            console.error(`Failed to parse JSON for key ${key}:`, error);
            parsedThemeData[key as keyof ThemeJsonData] = themeData[key];
          }
        }
      });
      setThemeJsonData(parsedThemeData as ThemeJsonData);
    } catch (error) {
      console.error("Error processing theme data:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchPageFields = () => {
    setIsSectionLoading(true);
    getThemePageFields()
      .then((res) => {
        setSectionList(res);
        setDistinctCards(extractCards(res));

        const result = {};

        res.filter(x => x.hasData).forEach(item => {
          const { card, category, data } = item;
          
          if (card && (category || category === '')) {
            const dataKey = `${card.replace(/\s+/g, '_')}${category ? `-${category.replace(/\s+/g, '_')}` : ''}`;
            
    
            if (!result[dataKey]) {
              result[dataKey] = [];
            }
    
            if (Array.isArray(data)) {
              result[dataKey] = result[dataKey].concat(data);
            }
          }
        });
        setFieldsData((prev) => result);
      })
      .catch((error) => {
        setIsSectionLoading(false);
        AlertMessage({ type: "error", msg: "Failed to load Section Fields" });
      })
      .finally(() => {
        setIsSectionLoading(false);
      });
  }; 

  const handleChange = (name: string, newValue: string | number) => {
    if (themeJsonData) {
      const existingValue = getNestedProperty(themeJsonData, name);
      
      if (typeof existingValue === "string") {
        if (!name.startsWith("ThemeJson")) {
          if (typeof newValue === 'string' && newValue.startsWith("#")) {
            newValue = newValue.split("#")[1];
          }
        }
        const updatedData = { ...themeJsonData };
        setNestedProperty(updatedData, name, newValue);
        setThemeJsonData(updatedData);
        setChangesMade(true);
        return true;
      } else if (typeof existingValue === "number") {
        const parsedValue = parseFloat(newValue.toString());
        
        if (!isNaN(parsedValue) && !/[a-zA-Z]/.test(newValue.toString())) {
          const updatedData = { ...themeJsonData };
          setNestedProperty(updatedData, name, parsedValue);
          setThemeJsonData(updatedData);
          setChangesMade(true);
          return true;
        }
      } else {
        console.warn(`Invalid Property ${name}`);
        return false;
      }
    } else {
      console.error('parsedThemeJson is not defined');
      return false;
    }
  };

  const handleCustomDataChange = (card: string, category: string, fieldKey: string, propertyKey: string, newValue: any) => {
    if (fieldsData) {
      const targetFieldKey = `${card.replace(/\s+/g, '_')}${category ? `-${category.replace(/\s+/g, '_')}` : ''}`;
  
      const targetField = fieldsData[targetFieldKey]?.find((item: any) => item.FieldKey === fieldKey);
  
      if (targetField) {
        const existingValue = targetField[propertyKey];
  
        if (typeof existingValue === "string") {
          if (typeof newValue === 'string' && newValue.startsWith("#")) {
            newValue = newValue.split("#")[1];
          }
          targetField[propertyKey] = newValue;
        } else if (typeof existingValue === "number") {
          targetField[propertyKey] = Number(newValue);
        } else if (existingValue === null) {
          targetField[propertyKey] = newValue;
        } else {
          console.warn(`Invalid Property for ${fieldKey} and property ${propertyKey}`);
          return false;
        }
  
        // Create a copy of the fieldsData and update the field
        const updatedData = { ...fieldsData };
        updatedData[targetFieldKey] = fieldsData[targetFieldKey].map((item: any) =>
          item.FieldKey === fieldKey ? targetField : item
        );
        
        // Update the state with the new data
        setFieldsData((prev) => updatedData);
        setChangesMade(true);
        return true;
      } else {
        console.error(`FieldKey ${fieldKey} not found in ${targetFieldKey}`);
        return false;
      }
    } else {
      console.error('fieldsData is not defined');
      return false;
    }
  };

  const handleSaveButtonClick = async () => {
    setIsLoading(true);
    //themeJsonData
    if (themeJsonData || fieldsData) {
      const updatedData: ThemeJsonData = { ...themeJsonData };
      (Object.keys(updatedData) as Array<keyof ThemeJsonData>).forEach((key) => {
        if (ThemesList.includes(key)) {
          try {
            updatedData[key] = JSON.stringify(
              updatedData[key],
              null,
              2
            ) as any;
          } catch (error) {
            console.error(`Failed to parse JSON for key ${key}:`, error);
          }
        }
      });

      // Custom Icon and Font Properties data
      const IconProperties: Array<typeof fieldsData[keyof typeof fieldsData][0]> = [];
      const FontProperties: Array<typeof fieldsData[keyof typeof fieldsData][0]> = [];

      Object.keys(fieldsData).forEach((key) => {
        const fieldArray = fieldsData[key];

        fieldArray.forEach((field) => {
          if ('Icon' in field && 'IconName' in field && 'IconType' in field) {
            IconProperties.push(field);
          } else if ('FontFamily' in field && 'FontSize' in field && 'FontWeight' in field) {
            FontProperties.push(field);
          }
        });
      });

      const finalPayload = {
        ThemeJson: updatedData,
        IconProperties,
        FontProperties,
      };

      const res = await updateThemeJson(finalPayload);
      if (res) {
        setChangesMade(false);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getFontFamilyMasterData();
    getFontSizeMasterData();
    getIconSizeMasterData();
    getFontWeightMasterData();
    fetchThemeJson();
    fetchPageFields();
  }, []);
  
  return (
    <>
      <Helmet>
        <title>Theme</title>
      </Helmet>
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 2,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid container alignItems="center" sx={{ my: 2 }} justifyContent="space-between">
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRight fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Theme
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    {!isSectionLoading && distinctCards.length > 0 && (<div className="switch-container">
                      <span className="theme-label">
                        {themeType === 'ThemeJsonLight' ? <b>Light</b> : <b>Dark</b>}
                      </span>
                      <Switch
                        checked={isDarkTheme}
                        onChange={(e, checked) => handleSliderChange(checked)}
                        color="primary"
                        inputProps={{
                          "aria-label": "toggle light and dark theme",
                        }}
                      />
                    </div>)}
                  </Grid>
                  <Grid item>
                    {changesMade && (
                      <Button
                        disabled={isLoading}
                        onClick={handleSaveButtonClick}
                        variant="contained"
                        sx={{
                          background: "linear-gradient(to bottom, #072f40, #072f40)",
                          minWidth: 100,
                        }}
                      >
                        Save
                        {isLoading && (<CircularProgress color="primary" thickness={5} size={20} sx={{ ml: 1 }} />)}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          {!isSectionLoading && distinctCards.length === 0 && (
            <Card sx={{ marginBottom: "2%", minHeight: "50%" }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography>Failed to get data!</Typography>
                </Box>
              </CardContent>
            </Card>
          )}
          {!isSectionLoading && distinctCards.length > 0 && distinctCards.map((card, idx) => {
              const filteredPickers = sectionList.filter((picker) => picker.card === card);
              return (
                <React.Fragment key={`${card}-${idx}`}>
                  <ThemeCard
                    title={card}
                    isFetching={isFetching}
                    fieldList={filteredPickers}
                    jsonData={themeJsonData}
                    themeType={themeType}
                    onChange={handleChange}
                    fontSizeOptions={fontSizeOptions}
                    fontFamilyOptions={fontFamilyOptions}
                    fontWeightOptions={fontWeightOptions}
                    iconSizeOptions={iconSizeOptions}
                    fieldsData={fieldsData}
                    isDarkMode={isDarkTheme}
                    handleCustomDataChange={handleCustomDataChange}
                  />
                </React.Fragment>
              );
            })}
        </Container>
      </Box>
    </>
  )
};

export default ThemeCreateEdit;
